import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { 
  Clipboard, Clock, CheckCircle, Play, Building, Users,
  ChevronRight, LogOut, User, Calendar, BarChart3, FileText,
  Home, FolderOpen, Settings, Menu, X, Bell
} from 'lucide-react';
import CNESSLogo from '../components/CNESSLogo';

// Question counts by organization size
const QUESTION_COUNTS = {
  'MICRO': { qualitative: 10, quantitative: 5, total: 15 },
  'SMALL': { qualitative: 10, quantitative: 10, total: 20 },
  'MEDIUM': { qualitative: 15, quantitative: 10, total: 25 },
  'LARGE': { qualitative: 15, quantitative: 15, total: 30 },
  'ENTERPRISE': { qualitative: 20, quantitative: 20, total: 40 }
};

const EvaluatorDashboard = () => {
  const [assignments, setAssignments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [evaluator, setEvaluator] = useState(null);
  const [activeFilter, setActiveFilter] = useState('all');
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const navigate = useNavigate();
  const location = useLocation();

  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

  useEffect(() => {
    // Get evaluator data from localStorage (persists across page refresh)
    const isAuthenticated = localStorage.getItem('cness_authenticated') === 'true';
    const userRole = localStorage.getItem('cness_user_role');
    const storedEvaluatorId = localStorage.getItem('user_id');
    
    if (!isAuthenticated || userRole !== 'evaluator' || !storedEvaluatorId) {
      // Mark redirect reason for login page
      sessionStorage.setItem('login_redirect', '/evaluator/dashboard');
      navigate('/login');
      return;
    }
    
    try {
      const userData = localStorage.getItem('userData');
      if (userData) {
        setEvaluator(JSON.parse(userData));
      }
    } catch (e) {
      console.error('Error parsing userData:', e);
    }

    fetchAssignments(storedEvaluatorId);
  }, [navigate]);

  const fetchAssignments = async (evaluatorId) => {
    if (!evaluatorId) {
      setLoading(false);
      return;
    }
    try {
      setLoading(true);
      console.log('[EvaluatorDashboard] Fetching assignments for:', evaluatorId);
      const response = await fetch(`${BACKEND_URL}/api/evaluator-assignments/${evaluatorId}`);
      
      if (!response.ok) {
        console.error('[EvaluatorDashboard] API error:', response.status);
        setAssignments([]);
        setLoading(false);
        return;
      }
      
      const data = await response.json();
      console.log('[EvaluatorDashboard] Response:', data);
      
      const assignmentsData = Array.isArray(data) ? data : (data.assignments || []);
      
      // Enrich with question counts based on org size
      const enrichedAssignments = assignmentsData.map(a => {
        const orgSize = (a.organization?.organization_size || a.organization_size || 'MEDIUM').toUpperCase();
        const questionCount = QUESTION_COUNTS[orgSize] || QUESTION_COUNTS['MEDIUM'];
        return {
          ...a,
          total_questions: questionCount.total,
          qualitative_count: questionCount.qualitative,
          quantitative_count: questionCount.quantitative
        };
      });
      
      // Remove duplicates
      const uniqueAssignments = enrichedAssignments.reduce((acc, curr) => {
        if (!acc.find(a => a.organization_id === curr.organization_id)) {
          acc.push(curr);
        }
        return acc;
      }, []);
      
      setAssignments(uniqueAssignments);
    } catch (error) {
      console.error('Error fetching assignments:', error);
      setAssignments([]);
    } finally {
      setLoading(false);
    }
  };

  const getStats = () => {
    const total = assignments.length;
    const pending = assignments.filter(a => !a.status || a.status === 'pending').length;
    const inProgress = assignments.filter(a => a.status === 'in_progress').length;
    const completed = assignments.filter(a => a.status === 'completed').length;
    return { total, pending, inProgress, completed };
  };

  const stats = getStats();

  const getFilteredAssignments = () => {
    switch (activeFilter) {
      case 'pending': return assignments.filter(a => !a.status || a.status === 'pending');
      case 'in_progress': return assignments.filter(a => a.status === 'in_progress');
      case 'completed': return assignments.filter(a => a.status === 'completed');
      default: return assignments;
    }
  };

  const handleLogout = () => {
    localStorage.clear();
    navigate('/login');
  };

  const handleStartEvaluation = (assignment) => {
    navigate(`/evaluator/assess/${assignment.organization_id}`, { state: { assignment } });
  };

  const getOrgSizeLabel = (size) => {
    const s = size?.toUpperCase();
    return { 'MICRO': 'Micro', 'SMALL': 'Small', 'MEDIUM': 'Medium', 'LARGE': 'Large', 'ENTERPRISE': 'Enterprise' }[s] || size || 'Medium';
  };

  const getStatusBadge = (status) => {
    const config = {
      'pending': { bg: 'bg-amber-100', text: 'text-amber-800', label: 'Pending' },
      'in_progress': { bg: 'bg-blue-100', text: 'text-blue-800', label: 'In Progress' },
      'completed': { bg: 'bg-green-100', text: 'text-green-800', label: 'Completed' }
    };
    const c = config[status] || config['pending'];
    return <span className={`px-3 py-1 rounded-full text-xs font-bold ${c.bg} ${c.text}`}>{c.label}</span>;
  };

  const filteredAssignments = getFilteredAssignments();
  const evaluatorName = evaluator?.name || evaluator?.full_name || evaluator?.username || 'Evaluator';

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-100">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading dashboard...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-100 flex">
      {/* Sidebar */}
      <aside className={`${sidebarOpen ? 'w-64' : 'w-20'} bg-slate-800 text-white transition-all duration-300 flex flex-col`}>
        {/* Logo */}
        <div className="p-4 border-b border-slate-700 flex items-center justify-between">
          {sidebarOpen ? (
            <div className="flex items-center gap-2">
              <div className="w-9 h-9 bg-white rounded-lg flex items-center justify-center p-1">
                <CNESSLogo variant="mark" className="!h-6" />
              </div>
              <span className="text-xl font-bold">CNESS</span>
            </div>
          ) : (
            <div className="w-9 h-9 bg-white rounded-lg flex items-center justify-center p-1 mx-auto">
              <CNESSLogo variant="mark" className="!h-6" />
            </div>
          )}
          <button onClick={() => setSidebarOpen(!sidebarOpen)} className="p-2 hover:bg-slate-700 rounded-lg">
            {sidebarOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </button>
        </div>

        {/* Navigation */}
        <nav className="flex-1 p-4">
          <ul className="space-y-2">
            <li>
              <Link
                to="/evaluator/dashboard"
                className={`flex items-center gap-3 px-4 py-3 rounded-lg transition-all ${
                  location.pathname === '/evaluator/dashboard' ? 'bg-indigo-600' : 'hover:bg-slate-700'
                }`}
              >
                <Home className="w-5 h-5" />
                {sidebarOpen && <span>Dashboard</span>}
              </Link>
            </li>
            <li>
              <Link
                to="/evaluator/organizations"
                className={`flex items-center gap-3 px-4 py-3 rounded-lg transition-all ${
                  location.pathname === '/evaluator/organizations' ? 'bg-indigo-600' : 'hover:bg-slate-700'
                }`}
              >
                <FolderOpen className="w-5 h-5" />
                {sidebarOpen && <span>Assigned Organizations</span>}
              </Link>
            </li>
            <li>
              <Link
                to="/evaluator/profile"
                className={`flex items-center gap-3 px-4 py-3 rounded-lg transition-all ${
                  location.pathname === '/evaluator/profile' ? 'bg-indigo-600' : 'hover:bg-slate-700'
                }`}
              >
                <User className="w-5 h-5" />
                {sidebarOpen && <span>My Profile</span>}
              </Link>
            </li>
          </ul>
        </nav>

        {/* User Info */}
        <div className="p-4 border-t border-slate-700">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-indigo-500 rounded-full flex items-center justify-center">
              <User className="w-5 h-5" />
            </div>
            {sidebarOpen && (
              <div className="flex-1 min-w-0">
                <p className="text-sm font-semibold truncate">{evaluatorName}</p>
                <p className="text-xs text-slate-400 truncate">Evaluator</p>
              </div>
            )}
          </div>
        </div>
      </aside>

      {/* Main Content */}
      <div className="flex-1 flex flex-col">
        {/* Header */}
        <header className="bg-white shadow-sm border-b px-6 py-4">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Evaluator Dashboard</h1>
              <p className="text-sm text-gray-500">Welcome back, {evaluatorName}</p>
            </div>
            <div className="flex items-center gap-4">
              <button className="p-2 hover:bg-gray-100 rounded-lg relative">
                <Bell className="w-5 h-5 text-gray-600" />
              </button>
              <button
                onClick={() => navigate('/evaluator/profile')}
                className="flex items-center gap-2 px-4 py-2 hover:bg-gray-100 rounded-lg"
              >
                <div className="w-8 h-8 bg-indigo-100 rounded-full flex items-center justify-center">
                  <User className="w-4 h-4 text-indigo-600" />
                </div>
              </button>
              <button
                onClick={handleLogout}
                className="flex items-center gap-2 px-4 py-2 text-red-600 hover:bg-red-50 rounded-lg"
              >
                <LogOut className="w-5 h-5" />
                <span className="hidden sm:inline">Logout</span>
              </button>
            </div>
          </div>
        </header>

        {/* Dashboard Content */}
        <main className="flex-1 p-6 overflow-auto">
          {/* Stats Cards */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
            <button
              onClick={() => setActiveFilter('all')}
              className={`bg-white rounded-xl shadow-sm p-6 text-left transition-all hover:shadow-md border-2 ${
                activeFilter === 'all' ? 'border-indigo-500 ring-2 ring-indigo-100' : 'border-transparent'
              }`}
            >
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 bg-indigo-100 rounded-xl flex items-center justify-center">
                  <Clipboard className="w-6 h-6 text-indigo-600" />
                </div>
                <div>
                  <p className="text-sm text-gray-500">Total Assigned</p>
                  <p className="text-3xl font-bold text-gray-900">{stats.total}</p>
                </div>
              </div>
            </button>

            <button
              onClick={() => setActiveFilter('pending')}
              className={`bg-white rounded-xl shadow-sm p-6 text-left transition-all hover:shadow-md border-2 ${
                activeFilter === 'pending' ? 'border-amber-500 ring-2 ring-amber-100' : 'border-transparent'
              }`}
            >
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 bg-amber-100 rounded-xl flex items-center justify-center">
                  <Clock className="w-6 h-6 text-amber-600" />
                </div>
                <div>
                  <p className="text-sm text-gray-500">Pending</p>
                  <p className="text-3xl font-bold text-amber-600">{stats.pending}</p>
                </div>
              </div>
            </button>

            <button
              onClick={() => setActiveFilter('in_progress')}
              className={`bg-white rounded-xl shadow-sm p-6 text-left transition-all hover:shadow-md border-2 ${
                activeFilter === 'in_progress' ? 'border-blue-500 ring-2 ring-blue-100' : 'border-transparent'
              }`}
            >
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center">
                  <BarChart3 className="w-6 h-6 text-blue-600" />
                </div>
                <div>
                  <p className="text-sm text-gray-500">In Progress</p>
                  <p className="text-3xl font-bold text-blue-600">{stats.inProgress}</p>
                </div>
              </div>
            </button>

            <button
              onClick={() => setActiveFilter('completed')}
              className={`bg-white rounded-xl shadow-sm p-6 text-left transition-all hover:shadow-md border-2 ${
                activeFilter === 'completed' ? 'border-green-500 ring-2 ring-green-100' : 'border-transparent'
              }`}
            >
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 bg-green-100 rounded-xl flex items-center justify-center">
                  <CheckCircle className="w-6 h-6 text-green-600" />
                </div>
                <div>
                  <p className="text-sm text-gray-500">Completed</p>
                  <p className="text-3xl font-bold text-green-600">{stats.completed}</p>
                </div>
              </div>
            </button>
          </div>

          {/* Organizations List */}
          <div className="bg-white rounded-xl shadow-sm">
            <div className="px-6 py-4 border-b">
              <h2 className="text-lg font-bold text-gray-900">
                {activeFilter === 'all' && 'All Assigned Organizations'}
                {activeFilter === 'pending' && 'Pending Organizations'}
                {activeFilter === 'in_progress' && 'In Progress Organizations'}
                {activeFilter === 'completed' && 'Completed Evaluations'}
                <span className="text-gray-500 font-normal ml-2">({filteredAssignments.length})</span>
              </h2>
            </div>

            {filteredAssignments.length === 0 ? (
              <div className="text-center py-16">
                <Building className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                <h3 className="text-lg font-semibold text-gray-900 mb-2">No Organizations Found</h3>
                <p className="text-gray-500">
                  {activeFilter === 'all' 
                    ? "You don't have any organizations assigned yet."
                    : `No organizations with "${activeFilter.replace('_', ' ')}" status.`}
                </p>
              </div>
            ) : (
              <div className="divide-y">
                {filteredAssignments.map((assignment, index) => {
                  const orgSize = (assignment.organization?.organization_size || assignment.organization_size || 'MEDIUM').toUpperCase();
                  const questionsScored = assignment.questions_scored || 0;
                  const totalQuestions = assignment.total_questions || QUESTION_COUNTS[orgSize]?.total || 25;
                  const progress = Math.round((questionsScored / totalQuestions) * 100);

                  return (
                    <div
                      key={assignment.assignment_id || index}
                      className="p-6 hover:bg-gray-50 transition-all cursor-pointer"
                      onClick={() => handleStartEvaluation(assignment)}
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-4">
                          <div className="w-12 h-12 bg-gradient-to-br from-indigo-500 to-purple-600 rounded-xl flex items-center justify-center text-white">
                            <Building className="w-6 h-6" />
                          </div>
                          <div>
                            <h3 className="text-lg font-semibold text-gray-900">
                              {assignment.organization_name || 'Organization'}
                            </h3>
                            <div className="flex items-center gap-4 text-sm text-gray-500 mt-1">
                              <span className="flex items-center gap-1">
                                <Users className="w-4 h-4" />
                                {getOrgSizeLabel(orgSize)}
                              </span>
                              <span className="flex items-center gap-1">
                                <Calendar className="w-4 h-4" />
                                {assignment.assigned_at ? new Date(assignment.assigned_at).toLocaleDateString() : 'N/A'}
                              </span>
                            </div>
                            <div className="flex items-center gap-3 mt-2">
                              {getStatusBadge(assignment.status)}
                              <span className="text-sm text-gray-600">
                                <span className="font-semibold">{questionsScored}</span>/{totalQuestions} questions evaluated
                              </span>
                            </div>
                          </div>
                        </div>

                        <div className="flex items-center gap-6">
                          {/* Progress */}
                          <div className="text-center">
                            <div className="relative w-14 h-14">
                              <svg className="transform -rotate-90 w-14 h-14">
                                <circle cx="28" cy="28" r="24" stroke="#e5e7eb" strokeWidth="4" fill="none" />
                                <circle
                                  cx="28" cy="28" r="24"
                                  stroke={progress === 100 ? '#22c55e' : '#6366f1'}
                                  strokeWidth="4"
                                  fill="none"
                                  strokeDasharray={`${2 * Math.PI * 24}`}
                                  strokeDashoffset={`${2 * Math.PI * 24 * (1 - progress / 100)}`}
                                  strokeLinecap="round"
                                />
                              </svg>
                              <span className="absolute inset-0 flex items-center justify-center text-sm font-bold">
                                {progress}%
                              </span>
                            </div>
                          </div>

                          {/* Action */}
                          <button className="flex items-center gap-2 px-5 py-2.5 bg-indigo-600 text-white rounded-lg font-semibold hover:bg-indigo-700 transition-all">
                            {assignment.status === 'completed' ? 'View Results' : assignment.status === 'in_progress' ? (
                              <><Play className="w-4 h-4" /> Continue</>
                            ) : (
                              <><Play className="w-4 h-4" /> Start</>
                            )}
                            <ChevronRight className="w-4 h-4" />
                          </button>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </main>
      </div>
    </div>
  );
};

export default EvaluatorDashboard;
